/**
 * DATOFORESPØRSELEN — arrangøren ber et spillested om en dato (SPILLESTED-KONSERT §2, skjerm 3 og 4).
 *
 * To kopier i to hus, speilet som budene: arrangørens `date_requests` og husets `venue_enquiries`
 * (migrering 0412). Huset ser dato, sal, oppsett, tider, artist, forventet publikum og meldingen —
 * aldri honoraret, som ikke står i noen av radene. Leien huset setter står i begge kopier.
 *
 * Et sted i stedsregisteret (utenfor Tvitvi) speiles ikke. Forespørselen lagres, og skjermen gir en
 * tekst og en lenke å kopiere, fordi e-post ikke finnes i Tvitvi ennå (§4).
 */
import { z } from 'zod';

import { dealVersionCardSchema } from './conversation-cards.js';

export const DATE_REQUEST_STATUSES = [
  'requested',
  'hold',
  'confirmed',
  'declined',
  'withdrawn',
  'released',
  'expired',
] as const;
export type DateRequestStatus = (typeof DATE_REQUEST_STATUSES)[number];

export const VENUE_ENQUIRY_STATUSES = [
  'new',
  'hold',
  'confirmed',
  'declined',
  'withdrawn',
  'released',
  'expired',
] as const;
export type VenueEnquiryStatus = (typeof VENUE_ENQUIRY_STATUSES)[number];

/** Fanene på husets side. «Avslått» samler også trukket tilbake, sluppet og utløpt. */
export const VENUE_ENQUIRY_TABS = ['waiting', 'hold', 'confirmed', 'declined'] as const;
export type VenueEnquiryTab = (typeof VENUE_ENQUIRY_TABS)[number];

export const VENUE_ENQUIRY_DECISIONS = ['hold', 'confirm', 'decline'] as const;
export type VenueEnquiryDecision = (typeof VENUE_ENQUIRY_DECISIONS)[number];

/** Grunnene basen kan avvise et svar med. Klienten viser `dateRequests.errors.<kode>`. */
export const DATE_REQUEST_REFUSALS = [
  'rank_taken',
  'rank_gap',
  'invalid_rank',
  'hold_until_past',
  'not_first',
  'date_taken',
  'enquiry_closed',
  'request_closed',
  'not_a_hold',
  'concert_already_confirmed',
  /* 224.5: «Forleng hold» må flytte utløpet fram i tid. */
  'hold_not_extended',
  /* 222.42: en passert dato kan ikke bes om hos en sal i Tvitvi (også fra «Ny konsert»). */
  'date_past',
  /* 222.31: «Trekk de andre tilbake» gjelder bare fra en bekreftet dato. */
  'not_confirmed',
  /* QA16 funn 3: noen står foran i køen. Svaret må si «likevel» (`passQueue`). */
  'queue_ahead',
] as const;
export type DateRequestRefusal = (typeof DATE_REQUEST_REFUSALS)[number];

const isoDate = z.iso.date();
const clock = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();
const count = z.number().int().min(0).max(1_000_000);

export const sendDateRequestSchema = z
  .object({
    /** Konserten forespørselen gjelder. Uten den lages en konsert (status «Forespurt»). */
    concertId: z.uuid().optional(),
    venueRoomId: z.uuid().optional(),
    /** En sal i plattformregisteret (1260). Går til Control Room til salen er i Tvitvi. */
    venueDirectoryRoomId: z.uuid().optional(),
    requestedOn: isoDate,
    layoutName: optionalText(120),
    capacity: count.nullable().optional(),
    doorsAt: clock.nullable().optional(),
    curfewAt: clock.nullable().optional(),
    /** Fra en booking, fritekst, eller tom («ikke avklart»). */
    artistName: optionalText(200),
    /**
     * QA15-S B1: innslaget fra «Hvem opptrer?» — en artist i Booking eller fra artistregisteret. En ny
     * konsert får den som hovedartist (en programpost, ikke en tekst), så Booking vet om forespørselen.
     */
    bookingArtistId: z.uuid().optional(),
    expectedAudience: count.nullable().optional(),
    message: optionalText(4000),
  })
  .refine(
    (body) => (body.venueRoomId === undefined) !== (body.venueDirectoryRoomId === undefined),
    {
      message: 'Exactly one of venueRoomId and venueDirectoryRoomId.',
      path: ['venueRoomId'],
    },
  )
  .meta({ id: 'SendDateRequest' });
export type SendDateRequest = z.infer<typeof sendDateRequestSchema>;

export const dateRequestDtoSchema = z
  .object({
    id: z.uuid(),
    concertId: z.uuid(),
    /**
     * `room`: en sal i Tvitvi (speilet). `directory`: en sal i plattformregisteret som ikke er i Tvitvi
     * ennå (1260) — Control Room tar den med til stedet og følger opp.
     */
    target: z.enum(['room', 'directory']),
    venueOrganizationId: z.uuid().nullable(),
    venueRoomId: z.uuid().nullable(),
    venueDirectoryRoomId: z.uuid().nullable(),
    venueName: z.string().nullable(),
    roomName: z.string().nullable(),
    city: z.string().nullable(),
    requestedOn: isoDate,
    layoutName: z.string().nullable(),
    capacity: z.number().int().nullable(),
    doorsAt: clock.nullable(),
    curfewAt: clock.nullable(),
    artistName: z.string().nullable(),
    expectedAudience: z.number().int().nullable(),
    message: z.string().nullable(),
    status: z.enum(DATE_REQUEST_STATUSES),
    holdRank: z.number().int().min(1).max(3).nullable(),
    holdUntil: isoDate.nullable(),
    /** Leien huset satte da det ga hold eller bekreftet. Arrangørens egen kopi. */
    rentMinor: z.number().int().nullable(),
    rentCurrency: z.string().length(3),
    rentTerms: z.string().nullable(),
    /** Husets andel av merch i prosent, fra husets svar (0433). */
    merchPercent: z.number().nullable(),
    answerNote: z.string().nullable(),
    sentAt: z.iso.datetime({ offset: true }).nullable(),
    answeredAt: z.iso.datetime({ offset: true }).nullable(),
    confirmedAt: z.iso.datetime({ offset: true }).nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    /** Bare for en registersal: organisasjonens egen kontakt på stedet (lagret i innmeldingen). */
    registryEmail: z.string().nullable(),
    registryContactName: z.string().nullable(),
  })
  .meta({ id: 'DateRequest' });
export type DateRequestDto = z.infer<typeof dateRequestDtoSchema>;

/** En konserts forespørsler. Få per konsert (én per dato og sted), derfor ikke paget. */
export const dateRequestListResponseSchema = z
  .object({ items: z.array(dateRequestDtoSchema) })
  .meta({ id: 'DateRequestListResponse' });
export type DateRequestListResponse = z.infer<typeof dateRequestListResponseSchema>;

export const listDateRequestsQuerySchema = z
  .object({ concertId: z.uuid() })
  .meta({ id: 'ListDateRequestsQuery' });
export type ListDateRequestsQuery = z.infer<typeof listDateRequestsQuerySchema>;

/** Artistene arrangøren kan velge i dialogen: fra bud til et byrå, med artist. */
export const dateRequestArtistOptionSchema = z
  .object({
    bookingArtistId: z.uuid(),
    artistName: z.string(),
    /** Budets status, så «Hold hos byrået» kan stå ved navnet. */
    bidStatus: z.string(),
    performanceDate: isoDate.nullable(),
  })
  .meta({ id: 'DateRequestArtistOption' });
export type DateRequestArtistOption = z.infer<typeof dateRequestArtistOptionSchema>;

export const dateRequestArtistOptionsSchema = z
  .object({ items: z.array(dateRequestArtistOptionSchema) })
  .meta({ id: 'DateRequestArtistOptions' });
export type DateRequestArtistOptions = z.infer<typeof dateRequestArtistOptionsSchema>;

/* ---------------------------------------------------------------------------------- huset */

export const venueEnquiryDtoSchema = z
  .object({
    id: z.uuid(),
    venueRoomId: z.uuid(),
    roomName: z.string(),
    /** Venue-beholderen salen hører til — adressen til husets side. */
    venueFestivalId: z.uuid(),
    requesterOrganizationId: z.uuid(),
    requesterName: z.string().nullable(),
    requesterPersonName: z.string().nullable(),
    /**
     * HVOR RADEN KOM FRA (QA15 C3): en datoforespørsel, eller husets eget bud på salen. Et bud-hold
     * avgjøres av budet; huset svarer ikke på det her. Valgfri for eldre klienter.
     */
    sourceKind: z.enum(['date_request', 'booking_bid']).optional(),
    sourceBidId: z.uuid().nullable().optional(),
    requestedOn: isoDate,
    /** Kveldens dato når den er bekreftet — den kan ha flyttet siden. */
    nightOn: isoDate.nullable(),
    layoutName: z.string().nullable(),
    capacity: z.number().int().nullable(),
    doorsAt: clock.nullable(),
    curfewAt: clock.nullable(),
    artistName: z.string().nullable(),
    expectedAudience: z.number().int().nullable(),
    message: z.string().nullable(),
    status: z.enum(VENUE_ENQUIRY_STATUSES),
    holdRank: z.number().int().min(1).max(3).nullable(),
    holdUntil: isoDate.nullable(),
    rentMinor: z.number().int().nullable(),
    rentCurrency: z.string().length(3),
    rentTerms: z.string().nullable(),
    merchPercent: z.number().nullable(),
    answerNote: z.string().nullable(),
    answeredAt: z.iso.datetime({ offset: true }).nullable(),
    venueNightId: z.uuid().nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    /**
     * 239.13: datoen er en ukedag salen holder stengt. En advarsel til huset, aldri en sperre.
     * Valgfri i typen fordi eldre svar og testfiksturer mangler den; API-et sender den alltid.
     */
    onClosedDay: z.boolean().optional(),
  })
  .meta({ id: 'VenueEnquiry' });
export type VenueEnquiryDto = z.infer<typeof venueEnquiryDtoSchema>;

export const venueEnquiryListResponseSchema = z
  .object({
    items: z.array(venueEnquiryDtoSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
  })
  .meta({ id: 'VenueEnquiryListResponse' });
export type VenueEnquiryListResponse = z.infer<typeof venueEnquiryListResponseSchema>;

export const listVenueEnquiriesQuerySchema = z
  .object({
    tab: z.enum(VENUE_ENQUIRY_TABS).default('waiting'),
    search: z.string().trim().max(200).optional(),
    /** QA15-S B3: forespørslene under ett spillesteds saler, eller én sal. Utelatt: hele huset. */
    festivalId: z.uuid().optional(),
    roomId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().max(512).optional(),
  })
  .meta({ id: 'ListVenueEnquiriesQuery' });
export type ListVenueEnquiriesQuery = z.infer<typeof listVenueEnquiriesQuerySchema>;

/** Tellingen på fanene, for de samme salene som lista (QA15-S B3). */
export const venueEnquirySummaryQuerySchema = z
  .object({ festivalId: z.uuid().optional(), roomId: z.uuid().optional() })
  .meta({ id: 'VenueEnquirySummaryQuery' });
export type VenueEnquirySummaryQuery = z.infer<typeof venueEnquirySummaryQuerySchema>;

/** Tallene på fanene. Tak på 99: det er et merke, ikke en telling. */
export const venueEnquirySummarySchema = z
  .object({ waiting: z.number().int(), hold: z.number().int() })
  .meta({ id: 'VenueEnquirySummary' });
export type VenueEnquirySummary = z.infer<typeof venueEnquirySummarySchema>;

/**
 * EN PLASS I SALENS KØ (QA16 298.5), slik `app.venue_room_occupancy` sier den: hvor den kom fra,
 * plassen, den andre parten og innslaget. Leses med `venueQueue.hold` / `venueQueue.requested`.
 */
export const venueQueueEntrySchema = z
  .object({
    refId: z.uuid(),
    source: z.enum(['date_request', 'booking_bid', 'night']),
    holdRank: z.number().int().nullable(),
    who: z.string().nullable(),
    act: z.string().nullable(),
  })
  .meta({ id: 'VenueQueueEntry' });
export type VenueQueueEntry = z.infer<typeof venueQueueEntrySchema>;

/** «Andre på denne datoen»: husets egne holds på samme sal og dato. */
export const venueDateHoldSchema = z
  .object({
    enquiryId: z.uuid(),
    requesterName: z.string().nullable(),
    artistName: z.string().nullable(),
    status: z.enum(VENUE_ENQUIRY_STATUSES),
    holdRank: z.number().int().nullable(),
    holdUntil: isoDate.nullable(),
  })
  .meta({ id: 'VenueDateHold' });
export type VenueDateHold = z.infer<typeof venueDateHoldSchema>;

export const venueEnquiryDetailSchema = z
  .object({
    enquiry: venueEnquiryDtoSchema,
    /** Andre forespørsler på samme sal og dato som er hold eller venter. */
    others: z.array(venueDateHoldSchema),
    /** Salens leie som forslag. Null når huset ikke har satt en, eller man ikke får se leien. */
    suggestedRentMinor: z.number().int().nullable(),
    suggestedRentCurrency: z.string().length(3),
    /** Salens vilkår og merch-andel som forslag, med samme regel som leien. */
    suggestedRentTerms: z.string().nullable(),
    suggestedMerchPercent: z.number().nullable(),
    /**
     * QA16 funn 3: det som står foran forespørselen på datoen (hold med bedre plass, husets eget
     * arrangement på hold), med køens ord. Tom: ingen å gå forbi. Valgfri for eldre klienter.
     */
    ahead: z.array(venueQueueEntrySchema).optional(),
  })
  .meta({ id: 'VenueEnquiryDetail' });
export type VenueEnquiryDetail = z.infer<typeof venueEnquiryDetailSchema>;

export const answerVenueEnquirySchema = z
  .object({
    decision: z.enum(VENUE_ENQUIRY_DECISIONS),
    /** 1., 2. eller 3. hold. Påkrevd for `hold`. */
    rank: z.number().int().min(1).max(3).optional(),
    holdUntil: isoDate.optional(),
    rentMinor: z.number().int().min(0).max(100_000_000_000).nullable().optional(),
    rentCurrency: z.string().length(3).optional(),
    rentTerms: optionalText(2000),
    merchPercent: z.number().min(0).max(100).nullable().optional(),
    note: optionalText(2000),
    /** QA16 funn 3 og 308.2: forbi køen, etter advarselen. Uten den: 409 `queue_ahead`. */
    passQueue: z.boolean().optional(),
  })
  .refine((body) => body.decision !== 'hold' || body.rank !== undefined, {
    message: 'A hold needs a rank.',
    path: ['rank'],
  })
  .meta({ id: 'AnswerVenueEnquiry' });
export type AnswerVenueEnquiry = z.infer<typeof answerVenueEnquirySchema>;

/** «Forleng hold» (224.5): ny utløpsdato, senere enn den som står. Rangen står. */
export const extendVenueHoldSchema = z
  .object({ holdUntil: isoDate })
  .meta({ id: 'ExtendVenueHold' });
export type ExtendVenueHold = z.infer<typeof extendVenueHoldSchema>;

/* -------------------------------------------------------------------------------- samtalen */

export const venueEnquiryMessageDtoSchema = z
  .object({
    id: z.uuid(),
    /** Skrevet av huset (`venue`) eller arrangøren (`requester`). */
    side: z.enum(['venue', 'requester']),
    authorName: z.string().nullable(),
    authorOrganizationName: z.string().nullable(),
    body: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    /** Skrevet av den som leser. */
    mine: z.boolean(),
    /** G2: kortet om leiearket (`deal_version`), skrevet av huset som handlet. Null for en melding. */
    card: dealVersionCardSchema.nullable().optional(),
  })
  .meta({ id: 'VenueEnquiryMessage' });
export type VenueEnquiryMessageDto = z.infer<typeof venueEnquiryMessageDtoSchema>;

export const venueEnquiryMessageListSchema = z
  .object({ items: z.array(venueEnquiryMessageDtoSchema) })
  .meta({ id: 'VenueEnquiryMessageList' });
export type VenueEnquiryMessageList = z.infer<typeof venueEnquiryMessageListSchema>;

export const postVenueEnquiryMessageSchema = z
  .object({ body: z.string().trim().min(1).max(4000) })
  .meta({ id: 'PostVenueEnquiryMessage' });
export type PostVenueEnquiryMessage = z.infer<typeof postVenueEnquiryMessageSchema>;
